import React, { useEffect, useMemo, useState } from "react";
import { SpriteIconWeb } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { ADD_BUTTON_VARIANT, IAddButtonProps, ICounterDirection } from "./types";
import {
    StyledAddButtonOuterContainer,
    StyledAddButtonHelperText,
    StyledAddButtonInnerContainer,
    StyledLeftButton,
    StyledRightButton,
    StyledCenterButton,
    StyledTopValue,
    StyledBottomValue,
    StyledCenterValue,
    StyledCenterStepper,
    IconDownChevron,
    Loader,
} from "./styled";

export { ADD_BUTTON_VARIANT };

export const AddButton = ({
    value,
    helperText,
    size,
    hasOptions,
    isUnavailable,
    unavailableText,
    unavailableTextColor = "text_Med_Emphasis",
    buttonText = "Add",
    updating,
    disableBoxShadow = false,
    customQtyColor = "special_positive",
    customTextColor = "special_positive",
    customBorderColor,
    isIncrementDisabled = false,
    isDecrementDisabled = false,
    addButtonVariant = ADD_BUTTON_VARIANT.DEFAULT,
    testIds,
    ...props
}: IAddButtonProps) => {
    const [lowerValue, setLowerValue] = useState(value - 1 <= 0 ? " " : value - 1);
    const [higherValue, setHigherValue] = useState(value + 1);

    const [previousValue, setPreviousValue] = useState(value);
    const [counterDirection, setCounterDirection] = useState<ICounterDirection>("stable");

    const primaryColor =
        addButtonVariant === ADD_BUTTON_VARIANT.POP || addButtonVariant === ADD_BUTTON_VARIANT.TOING
            ? "text_Highest_Emphasis"
            : "special_positive";

    useEffect(() => {
        if (value > previousValue) {
            setCounterDirection("increment");
        } else if (value < previousValue) {
            setCounterDirection("decrement");
        }
        const previousValueSetter = setTimeout(() => {
            setCounterDirection("stable");
        }, 100);

        setPreviousValue(value);

        return () => {
            clearTimeout(previousValueSetter);
        };
    }, [previousValue, value]);

    useEffect(() => {
        const lowerValueSetter = setTimeout(() => {
            setLowerValue(value - 1);
        }, 10);

        const higherValueSetter = setTimeout(() => {
            setHigherValue(value + 1);
        }, 10);

        return () => {
            clearTimeout(lowerValueSetter);
            clearTimeout(higherValueSetter);
        };
    }, [value]);

    const computedTextVariantForSize = useMemo(() => {
        if (size === "xSmall") {
            return "V2.body3.neutral.bold";
        } else if (size === "small") {
            return "V2.body2.neutral.bold";
        }
        return "V2.subheading1.neutral.bold";
    }, [size]);

    const computedUnavailableTextVariantForSize = useMemo(() => {
        if (size === "xSmall") {
            return "V2.overline.neutral.regular";
        } else if (size === "normal") {
            return "V2.body3.neutral.regular";
        }
        return "V2.caption.neutral.bold";
    }, [size]);

    return (
        <StyledAddButtonOuterContainer data-testid={testIds?.containerDataTestId}>
            <StyledAddButtonInnerContainer
                size={size}
                isUnavailable={isUnavailable}
                disableBoxShadow={disableBoxShadow}
                addButtonVariant={addButtonVariant}
                isIncrementDisabled={isIncrementDisabled}
                customBorderColor={customBorderColor}
            >
                <StyledLeftButton
                    size={size}
                    onClick={(e) => {
                        if (!isUnavailable && !isDecrementDisabled) props.onClickDecrement(e);
                    }}
                    isActive={value > 0}
                    className="add-button-left-container"
                    data-testid={testIds?.decrementCtaDataTestId}
                >
                    <Text
                        color={isDecrementDisabled ? "text_disabled" : primaryColor}
                        variant={computedTextVariantForSize}
                    >
                        &#8722;
                    </Text>
                </StyledLeftButton>
                <StyledCenterStepper>
                    <StyledTopValue size={size} isActive={lowerValue === value} aria-hidden={lowerValue !== value}>
                        <Text color="text_Highest_Emphasis" variant={computedTextVariantForSize}>
                            {Number(lowerValue) > 0 ? lowerValue : "Add"}
                        </Text>
                    </StyledTopValue>

                    {value > 0 ? (
                        <StyledCenterValue
                            size={size}
                            direction={counterDirection}
                            data-testid={testIds?.quantityDataTestId}
                        >
                            <Text color={customQtyColor} variant={computedTextVariantForSize}>
                                {value}
                            </Text>
                        </StyledCenterValue>
                    ) : (
                        <StyledCenterButton
                            onClick={(e) => {
                                if (!isUnavailable && !isIncrementDisabled) props.onClickIncrement(e);
                            }}
                            isActive={value <= 0}
                            size={size}
                            isUnavailable={isUnavailable}
                            className="add-button-center-container"
                        >
                            {isUnavailable ? (
                                <Text color={unavailableTextColor} variant={computedUnavailableTextVariantForSize}>
                                    {unavailableText || "Unavailable"}
                                </Text>
                            ) : (
                                <Text color={customTextColor} variant={computedTextVariantForSize}>
                                    {buttonText}
                                </Text>
                            )}
                            {hasOptions && (
                                <IconDownChevron>
                                    <SpriteIconWeb iconName="chevronDown8" />
                                </IconDownChevron>
                            )}
                        </StyledCenterButton>
                    )}
                    <StyledBottomValue size={size} isActive={higherValue === value} aria-hidden={higherValue !== value}>
                        <Text color={primaryColor} variant={computedTextVariantForSize}>
                            {higherValue}
                        </Text>
                    </StyledBottomValue>
                </StyledCenterStepper>
                <StyledRightButton
                    size={size}
                    onClick={(e) => {
                        if (!isUnavailable) props.onClickIncrement(e);
                    }}
                    isActive={value > 0}
                    className="add-button-right-container"
                    data-testid={testIds?.incrementCtaDataTestId}
                >
                    <Text
                        color={isIncrementDisabled ? "text_disabled" : primaryColor}
                        variant={computedTextVariantForSize}
                    >
                        &#x2B;
                    </Text>
                </StyledRightButton>
                {updating && <Loader size={size} data-testid={testIds?.loaderDataTestId} />}
            </StyledAddButtonInnerContainer>
            {helperText && (
                <StyledAddButtonHelperText>
                    <Text
                        variant={size === "normal" ? "V2.body3.neutral.regular" : "V2.caption.neutral.bold"}
                        color="text_Low_Emphasis"
                    >
                        {helperText}
                    </Text>
                </StyledAddButtonHelperText>
            )}
        </StyledAddButtonOuterContainer>
    );
};
